import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';

// Custom TikTok Icon component
const TikTokIcon = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { getLogoUrl, hasCustomLogo } = useSiteSettings();

  // Social media links with correct URLs
  const socialLinks = [
    { 
      name: 'TikTok',
      icon: TikTokIcon, 
      href: 'https://www.tiktok.com/@corporate.awards',
      ariaLabel: 'Follow us on TikTok'
    },
    { 
      name: 'LinkedIn',
      icon: Linkedin, 
      href: 'https://www.linkedin.com/company/corporate-awards-graphics/',
      ariaLabel: 'Follow us on LinkedIn'
    },
    { 
      name: 'Instagram',
      icon: Instagram, 
      href: 'https://www.instagram.com/engrave.ca/',
      ariaLabel: 'Follow us on Instagram'
    },
  ];
  
  return (
    <footer className="bg-ivory-100 border-t border-ivory-300/50">
      {/* Main footer content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              {hasCustomLogo ? (
                <img 
                  src={getLogoUrl()} 
                  alt="Engraveca" 
                  className="h-12 max-w-[180px] object-contain"
                  style={{ background: 'transparent' }}
                />
              ) : (
                <>
                  <div className="w-11 h-11 bg-gradient-to-br from-gold-600 to-gold-500 rounded-lg flex items-center justify-center shadow-luxury">
                    <span className="text-white font-heading font-bold text-xl">E</span>
                  </div>
                  <span className="text-2xl font-heading font-semibold text-ivory-800">Engraveca</span>
                </>
              )}
            </div>
            <p className="text-ivory-500 text-sm leading-relaxed mb-6 font-body">
              Your trusted partner for professional printing and engraving services. Quality products, competitive pricing, and exceptional customer service.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <a 
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={social.ariaLabel}
                  aria-label={social.ariaLabel}
                  className="w-10 h-10 bg-ivory-200 hover:bg-gradient-to-br hover:from-gold-600 hover:to-gold-500 rounded-lg flex items-center justify-center transition-all duration-300 group"
                  data-testid={`social-link-${social.name.toLowerCase()}`}
                >
                  <social.icon className="w-4 h-4 text-ivory-500 group-hover:text-white transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-ivory-800 font-heading font-semibold text-lg mb-6">Products</h3>
            <ul className="space-y-3">
              {[
                { label: 'Business Cards', href: '/products/business-cards' },
                { label: 'Flyers & Brochures', href: '/products/flyers-brochures' },
                { label: 'Marketing Materials', href: '/products/marketing-materials' },
                { label: 'Large Format', href: '/LargePrint' },
                { label: 'Awards', href: '/Awards' },
                { label: 'Promo Swag', href: '/Swag' },
              ].map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href} 
                    className="text-ivory-500 hover:text-gold-600 transition-colors text-sm flex items-center group font-body"
                  >
                    <ArrowRight className="w-3 h-3 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-gold-500" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-ivory-800 font-heading font-semibold text-lg mb-6">Company</h3>
            <ul className="space-y-3">
              {[
                { label: 'About Us', href: '/about' },
                { label: 'Why Choose Us', href: '/why-engraveca' },
                { label: 'Deals', href: '/promotions' },
                { label: 'Resources', href: '/resources' },
                { label: 'Contact Us', href: '/contact' },
              ].map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href} 
                    className="text-ivory-500 hover:text-gold-600 transition-colors text-sm flex items-center group font-body"
                  >
                    <ArrowRight className="w-3 h-3 mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-gold-500" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-ivory-800 font-heading font-semibold text-lg mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gold-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-gold-600" />
                </div>
                <div>
                  <div className="text-ivory-700 font-medium text-sm font-body">Phone</div>
                  <a href="tel:+18005210527" className="text-ivory-500 hover:text-gold-600 transition-colors text-sm font-body">
                    +1 800 521 0527
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gold-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-gold-600" />
                </div>
                <div>
                  <div className="text-ivory-700 font-medium text-sm font-body">Email</div>
                  <a href="mailto:sales@engraveca.com" className="text-ivory-500 hover:text-gold-600 transition-colors text-sm font-body">
                    sales@engraveca.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gold-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-gold-600" />
                </div>
                <div>
                  <div className="text-ivory-700 font-medium text-sm font-body">Address</div>
                  <div className="text-ivory-500 text-sm font-body">
                    1489 East Warner Avenue<br />
                    Santa Ana, CA 92705
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Elegant gold divider */}
        <div className="divider-gold mb-8" />

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-ivory-500 text-sm font-body">
            © {currentYear} Engraveca. All rights reserved.
          </p>
          <div className="flex gap-6">
            {[
              { label: 'Privacy Policy', href: '/privacy-policy' },
              { label: 'FAQ', href: '/faq' },
              { label: 'Terms of Service', href: '/terms' },
            ].map((link, index) => (
              <Link 
                key={index}
                to={link.href} 
                className="text-ivory-500 hover:text-gold-600 transition-colors text-sm font-body"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
